import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import "./LoginPage.css";

const RESEND_COOLDOWN_SECONDS = 30;
const DEFAULT_OTP_SENT_MESSAGE =
  "If this email is registered, a verification code has been sent.";
const DEFAULT_OTP_ERROR = "Invalid or expired verification code.";

function formatCooldown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function LoginPage() {
  // Get database from environment variable (hidden from user)
  const db = process.env.REACT_APP_ODOO_DATABASE || "ats";
  const { login: doLogin, requestOtp, verifyOtp, loading } =
    useContext(AuthContext);
  const navigate = useNavigate();

  // Overall mode: request a one-time code (default) or fall back to password.
  const [mode, setMode] = useState("otp"); // 'otp' | 'password'
  // Within OTP mode, which step of the flow we're on.
  const [otpStep, setOtpStep] = useState("email"); // 'email' | 'code'

  // OTP flow state
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [message, setMessage] = useState(null);

  // Password fallback state
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState(null);

  const codeInputRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // Autofocus the code input when we land on step 2.
  useEffect(() => {
    if (mode === "otp" && otpStep === "code" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [mode, otpStep]);

  // Countdown ticker for the resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      return;
    }
    cooldownTimerRef.current = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldown > 0]);

  const startCooldown = () => setCooldown(RESEND_COOLDOWN_SECONDS);

  const handleRequestOtp = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      setError(null);
      setSendingOtp(true);
      try {
        const res = await requestOtp(db, email);
        if (res && res.success) {
          setMessage(res.message || DEFAULT_OTP_SENT_MESSAGE);
          setOtpStep("code");
          setCode("");
          startCooldown();
        } else {
          setError((res && res.error) || "Unable to send code. Please try again.");
        }
      } catch (err) {
        setError("Connection error. Please check your network and try again.");
        console.error("Request OTP error:", err);
      } finally {
        setSendingOtp(false);
      }
    },
    [db, email, requestOtp]
  );

  const handleResend = useCallback(async () => {
    if (cooldown > 0 || resending) return;
    setError(null);
    setResending(true);
    try {
      const res = await requestOtp(db, email);
      if (res && res.success) {
        setMessage(res.message || DEFAULT_OTP_SENT_MESSAGE);
        startCooldown();
      } else {
        setError(
          (res && res.error) || "Unable to resend code right now. Please wait and try again."
        );
      }
    } catch (err) {
      setError("Connection error. Please check your network and try again.");
      console.error("Resend OTP error:", err);
    } finally {
      setResending(false);
    }
  }, [cooldown, resending, db, email, requestOtp]);

  const handleVerifyOtp = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (code.length !== 6 || verifyingOtp) return;
      setError(null);
      setVerifyingOtp(true);
      try {
        const res = await verifyOtp(db, email, code);
        if (!res || !res.success) {
          setError((res && res.error) || DEFAULT_OTP_ERROR);
          setCode("");
        } else {
          // PrivateRoute only redirects unauthenticated users off protected
          // routes; nothing moves an authenticated user off /login, so
          // navigate explicitly the way the password flow does.
          navigate("/quotes");
        }
      } catch (err) {
        setError("Connection error. Please check your network and try again.");
        console.error("Verify OTP error:", err);
      } finally {
        setVerifyingOtp(false);
      }
    },
    [code, verifyingOtp, db, email, verifyOtp, navigate]
  );

  // Auto-submit once 6 digits have been entered.
  useEffect(() => {
    if (
      mode === "otp" &&
      otpStep === "code" &&
      code.length === 6 &&
      !verifyingOtp
    ) {
      handleVerifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleChangeEmail = () => {
    setOtpStep("email");
    setCode("");
    setError(null);
    setMessage(null);
  };

  const handleCodeChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(digitsOnly);
    if (error) setError(null);
  };

  const switchToPassword = () => {
    setMode("password");
    setError(null);
    setMessage(null);
  };

  const switchToOtp = () => {
    setMode("otp");
    setOtpStep("email");
    setError(null);
    setMessage(null);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const res = await doLogin(db, login, password);
      if (res.success) {
        navigate("/quotes");
      } else {
        setError(res.error || "Login failed. Please try again.");
      }
    } catch (err) {
      setError("Connection error. Please check your network and try again.");
      console.error("Login error:", err);
    }
  };

  return (
    <div className="container-fluid login-container bg-info d-flex justify-content-center align-items-center row bg-opacity-25">
      <div className="login-panel bg-light p-5 rounded-3 d-flex justify-content-center align-items-center flex-column shadow col-12 col-md-6 col-lg-6 col-xl-3 ml-4">
        <h2>Login</h2>

        {mode === "otp" && otpStep === "email" && (
          <form onSubmit={handleRequestOtp} className="w-100">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                className="form-control rounded border-none"
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                required
                autoComplete="email"
                autoFocus
                disabled={sendingOtp}
              />
            </div>

            {(error || message) && (
              <p className={error ? "text-danger" : "login-message"} aria-live="polite">
                {error || message}
              </p>
            )}

            <button
              type="submit"
              disabled={sendingOtp}
              className="login-button btn btn-primary w-100 p-2"
            >
              {sendingOtp ? (
                <div className="d-flex align-items-center justify-content-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Sending…
                </div>
              ) : (
                "Send code"
              )}
            </button>

            <button
              type="button"
              className="login-link-button"
              onClick={switchToPassword}
            >
              Sign in with password instead
            </button>
          </form>
        )}

        {mode === "otp" && otpStep === "code" && (
          <form onSubmit={handleVerifyOtp} className="w-100">
            <p className="login-subtext">
              Enter the 6-digit code sent to <strong>{email}</strong>.{" "}
              <button
                type="button"
                className="login-inline-link"
                onClick={handleChangeEmail}
                disabled={verifyingOtp}
              >
                Change
              </button>
            </p>

            <div className="form-group">
              <label htmlFor="otp-code">Verification Code</label>
              <input
                ref={codeInputRef}
                className="form-control rounded border-none login-otp-input"
                id="otp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                required
                disabled={verifyingOtp}
              />
            </div>

            {(error || message) && (
              <p className={error ? "text-danger" : "login-message"} aria-live="polite">
                {error || message}
              </p>
            )}

            <button
              type="submit"
              disabled={verifyingOtp || code.length !== 6}
              className="login-button btn btn-primary w-100 p-2"
            >
              {verifyingOtp ? (
                <div className="d-flex align-items-center justify-content-center">
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Verifying…
                </div>
              ) : (
                "Verify & sign in"
              )}
            </button>

            <button
              type="button"
              className="login-link-button"
              onClick={handleResend}
              disabled={cooldown > 0 || resending}
            >
              {cooldown > 0
                ? `Resend in ${formatCooldown(cooldown)}`
                : resending
                ? "Resending…"
                : "Resend code"}
            </button>
          </form>
        )}

        {mode === "password" && (
          <form onSubmit={handlePasswordSubmit} className="w-100">
            <div className="form-group">
              <label htmlFor="login">Username</label>
              <input
                className="form-control rounded border-none"
                id="login"
                type="text"
                value={login}
                onChange={(e) => {
                  setLogin(e.target.value);
                  if (error) {
                    setError(null);
                  }
                }}
                required
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                className="form-control rounded border-none"
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) {
                    setError(null);
                  }
                }}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p className="text-danger" aria-live="polite">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="login-button btn btn-primary w-100 p-2 "
            >
              {loading ? (
                <div className="d-flex align-items-center justify-content-center ">
                  <div
                    className="spinner-border spinner-border-sm me-2 "
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Logging in…
                </div>
              ) : (
                "Log In"
              )}
            </button>

            <button
              type="button"
              className="login-link-button"
              onClick={switchToOtp}
            >
              Sign in with a code instead
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
